import React, { useMemo, useState } from "react";
import { Box, Typography } from "@mui/material";
import type { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import Button from "@components/Button/Button";
import LoadingOverlay from "@components/LoadingOverlay/LoadingOverlay";
import { useNotification } from "@context/NotificationContext";
import type { DelayView } from "@services/drybulkCargoOperationsApi";
import { useTranslation } from "react-i18next";
import OperationDataGrid from "../components/OperationDataGrid";
import RowActionsMenu from "../components/RowActionsMenu";
import { DELAY_WRITABLE_STATUSES, DRYBULK_CARGO_OPS_LANG_BASE } from "../constants";
import { useDelayMutations } from "../hooks/useDelayMutations";
import { useOperationDelays } from "../hooks/useOperationDelays";
import { useGatedOpen } from "../hooks/usePollingGate";
import { formatInstant } from "../utils/formatInstant";
import { formatMinutes } from "../utils/formatMinutes";
import { problemMessage } from "../utils/problemDetail";
import DelayDrawer from "./DelayDrawer";
import DeleteDelayDialog from "./DeleteDelayDialog";

const L = `${DRYBULK_CARGO_OPS_LANG_BASE}.detail.delays`;
const text = (value: string | number | null | undefined) =>
  value === null || value === undefined || value === "" ? "-" : String(value);

/**
 * DBCO-0059 (R-30, AD-96) - the Delays tab: the total, the grid in the order the API returns, and Add / Update / Remove while the
 * operation is Ongoing or Waiting Final Draft Survey. Outside those statuses the Add button and the Action column are absent, not
 * disabled. Every duration is the server's; the client only formats it.
 */
const OperationDelaysTab: React.FC<{ operationId: string; status: string | null | undefined }> = ({
  operationId,
  status,
}) => {
  const { t } = useTranslation();
  const { notify } = useNotification();
  const delays = useOperationDelays(operationId);
  const { remove } = useDelayMutations(operationId);
  const writable = DELAY_WRITABLE_STATUSES.includes(status ?? "");
  const [drawer, setDrawer] = useState<{ delay: DelayView | null } | null>(null);
  const [removing, setRemoving] = useState<DelayView | null>(null);
  useGatedOpen(drawer !== null || removing !== null);

  const confirmRemove = async () => {
    if (!removing) {
      return;
    }
    try {
      await remove.mutateAsync(removing.id);
      notify({ type: "success", title: t(`${L}.removed`) });
      setRemoving(null);
    } catch (error) {
      notify({
        type: "error",
        title: t("common.error"),
        description: problemMessage(error) ?? t(`${L}.remove-failed`),
      });
    }
  };

  const columns = useMemo<GridColDef[]>(() => {
    const base: GridColDef[] = [
      { field: "no", headerName: t(`${L}.columns.no`), width: 60 },
      { field: "holdNo", headerName: t(`${L}.columns.hold-no`), width: 90 },
      { field: "delayTypeCode", headerName: t(`${L}.columns.delay-type`), flex: 1, minWidth: 120 },
      {
        field: "delayCode",
        headerName: t(`${L}.columns.delay-code`),
        flex: 1.5,
        minWidth: 180,
      },
      { field: "startAt", headerName: t(`${L}.columns.start`), flex: 1, minWidth: 140 },
      { field: "endAt", headerName: t(`${L}.columns.end`), flex: 1, minWidth: 140 },
      { field: "durationMinutes", headerName: t(`${L}.columns.time`), width: 100 },
      { field: "loaderCode", headerName: t(`${L}.columns.loader`), width: 100 },
      { field: "remarks", headerName: t(`${L}.columns.remarks`), flex: 1.5, minWidth: 160 },
    ];
    if (!writable) {
      return base;
    }
    return [
      ...base,
      {
        field: "action",
        headerName: t(`${L}.columns.action`),
        width: 90,
        renderCell: (params: GridRenderCellParams) => (
          <RowActionsMenu
            testId={`delay-menu-${params.row.id}`}
            actions={[
              {
                key: "update",
                label: t(`${L}.update`),
                onSelect: () => setDrawer({ delay: params.row.delay as DelayView }),
              },
              {
                key: "remove",
                label: t(`${L}.remove`),
                onSelect: () => setRemoving(params.row.delay as DelayView),
              },
            ]}
          />
        ),
      },
    ];
  }, [t, writable]);

  const rows = (delays.data?.delays ?? []).map((delay, index) => ({
    id: delay.id,
    no: index + 1,
    holdNo: delay.holdNo,
    delayTypeCode: delay.delayTypeCode,
    delayCode: `${delay.delayCode} - ${delay.delayDescription ?? ""}`,
    startAt: formatInstant(delay.startAt),
    endAt: formatInstant(delay.endAt),
    durationMinutes: formatMinutes(delay.durationMinutes),
    loaderCode: text(delay.loaderCode),
    remarks: text(delay.remarks),
    delay,
  }));

  return (
    <Box
      data-testid="operation-delays-tab"
      sx={{ display: "flex", flexDirection: "column", gap: 2, position: "relative" }}
    >
      <LoadingOverlay loading={delays.isLoading} />
      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
        <Typography variant="subtitle1" component="span">
          {t(`${L}.total-label`)}
        </Typography>
        <Typography variant="h6" component="span" data-testid="operation-delays-total">
          {t(`${L}.total-value`, {
            value: formatMinutes(delays.data?.totalDurationMinutes ?? 0),
          })}
        </Typography>
        {writable && (
          <Box sx={{ ml: "auto" }}>
            <Button
              variant="contained"
              testId="operation-delays-add"
              onClick={() => setDrawer({ delay: null })}
              label={<span className="normal-case font-medium">{t(`${L}.add`)}</span>}
            />
          </Box>
        )}
      </Box>
      {delays.isError && (
        <Typography data-testid="operation-delays-unavailable" color="text.secondary">
          {t(`${L}.unavailable`)}
        </Typography>
      )}
      <OperationDataGrid
        title={t(`${L}.caption`)}
        testId="operation-delays-grid"
        columns={columns}
        plainFields={["action"]}
        rows={rows}
        emptyMessage={t(`${L}.empty`)}
      />
      {drawer && (
        <DelayDrawer
          key={drawer.delay?.id ?? "new"}
          operationId={operationId}
          delay={drawer.delay}
          onClose={() => setDrawer(null)}
        />
      )}
      {removing && (
        <DeleteDelayDialog
          delay={removing}
          isSubmitting={remove.isPending}
          onConfirm={() => void confirmRemove()}
          onCancel={() => setRemoving(null)}
        />
      )}
    </Box>
  );
};

export default OperationDelaysTab;
